import { HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { GbPaginatedResponse } from '@galabid/gb-dashboard/core/data-access'
import { ComponentStore } from '@ngrx/component-store'
import { tapResponse } from '@ngrx/operators'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { PresenterWidgetsApiService } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets-api.service'
import { HonourRollActivity } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbErrorMessageService } from 'libs/shared/utils/helpers/src/lib/gb-error-message/gb-error-message.service'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { switchMap, tap, withLatestFrom } from 'rxjs/operators'

interface State {
  activities: HonourRollActivity[]
  loading: boolean
  error?: HttpErrorResponse
  count: number
  next: string | null
  previous: string | null
}

@Injectable({
  providedIn: 'root',
})
export class GbHonourRollStore extends ComponentStore<State> {
  readonly loading$ = this.select((state) => state.loading)
  readonly activities$ = this.select((state) => state.activities)
  readonly count$ = this.select((state) => state.count)
  readonly next$ = this.select((state) => state.next)
  readonly previous$ = this.select((state) => state.previous)
  readonly LOADER_ID = 'honourRollActivities'

  constructor(
    private presenterWidgetsApiService: PresenterWidgetsApiService,
    private selectedAuctionFacade: SelectedAuctionFacade,
    private gbNotificationService: GbNotificationService,
    private gbErrorMessageService: GbErrorMessageService,
  ) {
    super({ activities: [], loading: false, count: 0, next: null, previous: null })
  }

  /** -------------------------
   * Pure state updaters
   * ------------------------- */
  setActivities(response: GbPaginatedResponse<HonourRollActivity>) {
    this.patchState({
      activities: response.results,
      count: response.count,
      next: response.next,
      previous: response.previous,
      loading: false,
    })
  }

  addHonourRollActivity(activity: HonourRollActivity) {
    this.patchState((state) => ({
      activities: [activity, ...state.activities],
      count: state.count + 1,
    }))
  }

  removeHonourRollActivity(uuid: string) {
    this.patchState((state) => ({
      activities: state.activities.filter((a) => a.uuid !== uuid),
      count: Math.max(0, state.count - 1),
    }))
  }

  clearActivities() {
    this.patchState({ activities: [], count: 0, next: null, previous: null })
  }

  /** -------------------------
   * Effects with API calls
   * ------------------------- */
  loadHonourRollActivities = this.effect<{
    widget: string
    limit: number
    offset?: number
    ordering?: string
  }>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      tap(() => this.patchState({ loading: true })),
      switchMap(([{ widget, limit, offset = 0, ordering = '-created' }, urlName]) =>
        this.presenterWidgetsApiService.getHonourRollActivities(urlName, widget, limit, offset, ordering).pipe(
          tapResponse({
            next: (response) => this.setActivities(response),
            error: (error: HttpErrorResponse) => this.handleError(error),
          }),
        ),
      ),
    ),
  )

  /** -------------------------
   * Private helpers
   * ------------------------- */
  private handleError(error: HttpErrorResponse): void {
    const message = this.gbErrorMessageService.getErrorMessage(error.error)
    this.gbNotificationService.error(message || 'GENERIC.ERROR')
    this.patchState({ error, loading: false })
  }
}
